import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SpecimenView } from "./SpecimenView";

const NUMBER = new Intl.NumberFormat();
const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

const SpecimenList = ({ browser }) => {
  const { items, total, page, pageSize, listLoading } = browser;
  const pages = Math.ceil(total / pageSize);
  if (listLoading && !items.length) return <CircularProgress size={24} sx={{ m: 3 }} />;
  if (!total) return <Typography color="text.secondary" sx={{ p: 2 }}>No specimens match.</Typography>;
  return (
    <Box>
      <List dense disablePadding aria-label="Specimens">
        {items.map((item, i) => (
          <ListItemButton key={`${item.trayId}-${item.specimenId}`} onClick={() => browser.showSpecimen(page * pageSize + i)} divider>
            <ListItemText
              primary={item.species}
              primaryTypographyProps={{ sx: { fontStyle: item.category === "species" ? "italic" : "normal" } }}
              secondary={[item.reportDate && DAY.format(new Date(`${item.reportDate}T00:00:00Z`)), item.sex, `Tray ${item.trayId}, position ${item.position + 1}`]
                .filter(Boolean)
                .join(" · ")}
            />
          </ListItemButton>
        ))}
      </List>
      {pages > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1 }}>
          <Button size="small" disabled={page === 0} onClick={() => browser.goToPage(page - 1)} startIcon={<ChevronLeftIcon />}>
            Newer
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {pages}
          </Typography>
          <Button size="small" disabled={page >= pages - 1} onClick={() => browser.goToPage(page + 1)} endIcon={<ChevronRightIcon />}>
            Older
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * The specimen panel: the specimens behind a count, or one specimen at a
 * time with its images, stepping through the report's list. `children`
 * renders under the result (the contributed ID editor).
 */
export const SpecimenDrawer = ({ browser, renderEditor }) => {
  const { source, view, total, index, specimen, specimenLoading, error } = browser;
  const fromList = Boolean(source?.filter);
  return (
    <Drawer
      anchor="right"
      open={browser.isOpen}
      onClose={browser.close}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, display: "flex", flexDirection: "column" } }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
        {view === "specimen" && fromList && (
          <Tooltip title="Back to the list">
            <IconButton aria-label="Back to the list" onClick={browser.showList} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500 }} noWrap>
            {source?.title ?? "Specimens"}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="specimen-position">
            {view === "specimen" && total
                ? `${NUMBER.format(index + 1)} of ${NUMBER.format(total)}`
                : `${NUMBER.format(total)} specimen${total === 1 ? "" : "s"}`}
          </Typography>
        </Box>
        {view === "specimen" && (
          <>
            <Tooltip title="Previous specimen">
              <span>
                <IconButton aria-label="Previous specimen" disabled={!browser.hasPrevious} onClick={browser.previous}>
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next specimen">
              <span>
                <IconButton aria-label="Next specimen" disabled={!browser.hasNext} onClick={browser.next}>
                  <ChevronRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="Close">
          <IconButton aria-label="Close specimens" onClick={browser.close}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", px: view === "specimen" ? 2 : 0, py: view === "specimen" ? 2 : 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {view === "list" ? (
          <SpecimenList browser={browser} />
        ) : specimenLoading && !specimen ? (
          <CircularProgress size={24} sx={{ m: 3 }} />
        ) : specimen ? (
          <Box sx={{ opacity: specimenLoading ? 0.5 : 1, transition: "opacity 120ms" }}>
            <SpecimenView data={specimen}>{renderEditor?.(specimen)}</SpecimenView>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};
